import {
  Avatar,
  Box,
  FlexBox,
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { getAsyncStatusFromRtkQueryStates } from 'lib-shared-auth';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce, useUnmount } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { appActions } from 'app/app.actions';
import { ConversationChannelIcon } from 'components/conversation/conversationChannelIcon';
import {
  CommandLauncherAction,
  CommandLauncherScope,
  useCommandLauncher,
} from 'utils/commandLauncher';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { getUserDisplayName } from 'utils/user';

import styles from '../../commandLauncher.module.scss';
import { getCommandLauncherActionItems } from '../../utils/getCommandLauncherActionSelectionListItems';

/** Represents the conversation scope list of the command launcher. */
export const ConversationScopeList = (): JSX.Element => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const {
    isActive,
    scope,
    scopeQuery,
    createAction,
    performAction,
    registerActions,
    setAsyncStatus,
  } = useCommandLauncher();

  const isInScope = scope === CommandLauncherScope.Conversation;

  const [
    readConversation,
    {
      conversation,
      requester,
      subject,
      channel,
      isUninitialized,
      isFetching,
      isSuccess,
      isError,
    },
  ] = conversationsApi.useLazyReadConversationQuery({
    selectFromResult: ({ data, ...rest }) => {
      const conversation = data?.data?.history.main_conversation;

      return {
        ...rest,
        conversation,
        requester: conversation
          ? conversation.authors[conversation.requested_by]
          : undefined,
        subject: conversation?.attributes?.subject,
        channel: conversation?.initial_channel,
      };
    },
  });

  const [actions, setActions] = useState<readonly CommandLauncherAction[]>([]);

  const handleSelectionListOnChange = (
    event: SelectionListChangeEvent<CommandLauncherAction>,
  ) => {
    if (event.selectedItem.isNode) {
      return;
    }

    performAction(event.selectedItem.value);
    dispatch(appActions.hideModal({ type: 'app:commandLauncher' }));
  };

  // Handles fetching the conversation.
  useDebounce(
    () => {
      if (isInScope && scopeQuery && !isNaN(parseInt(scopeQuery))) {
        readConversation({ csid: parseInt(scopeQuery) });
      }
    },
    500,
    [isInScope, readConversation, scopeQuery],
  );

  // Handles creating the actions.
  useEffect(() => {
    if (!conversation) return;

    setActions([
      createAction({
        label: 'View conversation',
        icon: 'chat',
        performFn: () => {
          navigate({
            path: '/conversation/:csid',
            params: { path: { csid: `${conversation.id}` } },
          });
        },
      }),
      createAction({
        label: 'View activity log',
        icon: 'activity',
        performFn: () => {
          navigate({
            path: '/conversation/:csid/activity-log',
            params: { path: { csid: `${conversation.id}` } },
          });
        },
      }),
      createAction({
        label: 'View requester',
        icon: 'user',
        performFn: () => {
          const requestedBy = conversation?.requested_by;

          if (requestedBy) {
            navigate({
              path: '/contacts/user/:id',
              params: { path: { id: requestedBy } },
            });
          }
        },
      }),
    ]);
  }, [createAction, navigate, conversation]);

  // Handles registering the actions.
  useEffect(() => {
    const unregister = registerActions(actions);
    return () => unregister();
  }, [actions, registerActions]);

  const asyncStatus = useMemo(
    () =>
      getAsyncStatusFromRtkQueryStates({
        isError,
        isSuccess,
        isFetching,
        isUninitialized,
      }),
    [isError, isSuccess, isFetching, isUninitialized],
  );

  useEffect(() => setAsyncStatus(asyncStatus), [asyncStatus, setAsyncStatus]);

  useUnmount(() => setAsyncStatus('fulfilled'));

  if (!isInScope) {
    return <></>;
  }

  const hasNoQuery = !scopeQuery || scopeQuery.trim().length === 0;
  const hasValidQuery =
    !!scopeQuery &&
    scopeQuery.trim().length > 0 &&
    !isNaN(parseInt(scopeQuery));
  const hasResults = actions.length > 0;

  const renderEmptyState = (message: string) => (
    <Box padding="300" className={styles.emptyState}>
      <span>{message}</span>
    </Box>
  );

  if (hasNoQuery) {
    return renderEmptyState('Type a conversation ID to find a conversation');
  }

  if (!hasValidQuery) {
    return renderEmptyState('Please enter a valid conversation ID');
  }

  if (isUninitialized || isFetching) {
    return renderEmptyState('Finding the conversation...');
  }

  if (isError || !hasResults) {
    return renderEmptyState('No conversation found');
  }

  return (
    <>
      {conversation && requester && (
        <Box padding="300">
          <FlexBox alignItems="center" gap="300">
            <Avatar size="32" user={requester} />

            <FlexBox flexDirection="column" gap="100">
              <FlexBox alignItems="center" gap="200">
                {channel && (
                  <ConversationChannelIcon
                    channel={
                      convertChannelCasing(
                        channel,
                        'truncate',
                      ) as ConversationsChannels
                    }
                  />
                )}

                <span className={styles.conversationPreviewTitle}>
                  {subject || `Conversation #${conversation.id}`}
                </span>
              </FlexBox>

              <span className={styles.conversationPreviewSubtitle}>
                {getUserDisplayName(requester)}
              </span>
            </FlexBox>
          </FlexBox>
        </Box>
      )}

      <SelectionList isActive={isActive} onChange={handleSelectionListOnChange}>
        {getCommandLauncherActionItems(actions)}
      </SelectionList>
    </>
  );
};
